import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Settings, ChevronDown, Info } from 'lucide-react';

export function AdvancedOptions({ results }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const costItems = [
    { label: t('advanced.labor'),    value: results?.costBreakdown?.labor,    color: '#78B928' },
    { label: t('advanced.audit'),    value: results?.costBreakdown?.audit,    color: '#78B928' },
    { label: t('advanced.training'), value: results?.costBreakdown?.training, color: '#2867B2' },
    { label: t('advanced.tools'),    value: results?.costBreakdown?.tools,    color: '#66707A' },
  ];

  const total = costItems.reduce((s, i) => s + (i.value || 0), 0);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="bg-white border-[#DDE1E5]" data-testid="advanced-options-section">
        <CollapsibleTrigger asChild>
          <button type="button" className="w-full p-6 text-left hover:bg-[#F5F6F7] transition-colors" data-testid="advanced-options-toggle">
            <CardTitle
              className="flex items-center justify-between text-lg text-[#171A1D]"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <span className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#66707A]" />
                {t('advanced.title')}
              </span>
              <ChevronDown className={`w-5 h-5 text-[#8A929B] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </CardTitle>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-0">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
              <Info className="w-4 h-4 text-[#527F19] mt-0.5 shrink-0" />
              <p className="text-xs text-[#66707A] leading-relaxed">
                {t('advanced.costMethodNote')}
              </p>
            </div>
            {costItems.map(({ label, value, color }) => {
              const pct = total > 0 ? Math.round((value / total) * 100) : 0;
              return (
                <div key={label}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-[#171A1D]">{label}</span>
                    <span className="text-sm" style={{ color, fontFamily: 'IBM Plex Mono, monospace' }}>
                      {value?.toLocaleString() ?? 0} ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-[#F5F6F7] overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
            {results?.auditDays && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
                <Info className="w-4 h-4 text-[#2867B2] mt-0.5 shrink-0" />
                <p className="text-xs text-[#66707A] leading-relaxed">
                  {t('advanced.auditNote', {
                    defaultValue: 'Audit days ({{days}}) come from the ISMSCalculator audit-time model.',
                    days: results.auditDays,
                  })}
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
